import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 1000;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    document.addEventListener('pointerdown', this.#handlePointerDownOutside, {
      capture: true,
      signal: this.#abortController.signal,
    });

    document.addEventListener('keydown', this.#handleKeyDown, {
      signal: this.#abortController.signal,
    });

    this.addEventListener(
      'mouseover',
      (event) => {
        if (!(event.target instanceof Element)) return;

        if (this.#state.activeItem && this.contains(event.target)) {
          this.#debouncedDeactivate.cancel();
        }

        const link = event.target.closest('.menu-list__link[aria-haspopup]');
        if (!link || !this.contains(link)) return;
        this.activate(event);
      },
      { signal: this.#abortController.signal }
    );

    this.addEventListener(
      'mouseout',
      (event) => {
        const related = event.relatedTarget;
        if (related instanceof Node && this.contains(related)) return;
        if (!this.#state.activeItem) return;
        this.#debouncedDeactivate();
      },
      { signal: this.#abortController.signal }
    );

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    this.#clearCloseAnimation();
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /** @type {number | null} */
  #closeAnimationTimer = null;

  /** @type {HTMLElement | null} */
  #closeTransitionTarget = null;

  /** @type {((event: TransitionEvent) => void) | null} */
  #closeTransitionHandler = null;

  #isClosing = false;

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#clearCloseAnimation();
    this.#state.activeItem?.removeAttribute('data-animating');

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item) return;

    const slotHost =
      event.target instanceof Element ? event.target.closest('.menu-list__list-item') : null;
    const isDefaultSlot = slotHost ? slotHost.slot === '' : true;

    const currentHeight = Number.parseFloat(this.style.getPropertyValue('--submenu-height'));
    if (item === this.#state.activeItem && currentHeight > 0) return;

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem && previouslyActiveItem !== item) {
      previouslyActiveItem.ariaExpanded = 'false';
      previouslyActiveItem.removeAttribute('data-animating');
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    const submenu = this.#resolveSubmenuPanel(item, isDefaultSlot);
    const overflowMenuHeight = this.overflowMenu?.scrollHeight ?? 0;

    const submenuHeight = submenu ? this.#readSubmenuHeight(submenu, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * @param {HTMLElement} submenu
   * @param {number} overflowMenuHeight
   */
  #readSubmenuHeight(submenu, overflowMenuHeight = 0) {
    submenu.classList.add('menu-list__submenu--measure');
    try {
      return Math.max(submenu.scrollHeight, overflowMenuHeight);
    } finally {
      submenu.classList.remove('menu-list__submenu--measure');
    }
  }

  #removeCloseTransitionListener() {
    if (this.#closeTransitionTarget && this.#closeTransitionHandler) {
      this.#closeTransitionTarget.removeEventListener('transitionend', this.#closeTransitionHandler);
    }
    this.#closeTransitionTarget = null;
    this.#closeTransitionHandler = null;
  }

  #clearCloseAnimation() {
    if (this.#closeAnimationTimer != null) {
      window.clearTimeout(this.#closeAnimationTimer);
      this.#closeAnimationTimer = null;
    }
    this.#removeCloseTransitionListener();
    this.#isClosing = false;
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} [isDefaultSlot]
   * @returns {HTMLElement | null}
   */
  #resolveSubmenuPanel(item, isDefaultSlot = true) {
    const submenu = findSubmenu(item);
    if (submenu) return submenu;

    if (!isDefaultSlot) {
      return this.overflowMenu;
    }

    const slotHost = item.closest('.menu-list__list-item');
    if (slotHost instanceof HTMLElement && slotHost.slot === 'overflow') {
      return this.overflowMenu;
    }

    return null;
  }

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Element) {
      const listItem = event.target.closest('.menu-list__list-item');
      if (listItem?.contains(relatedTarget)) return;
    }

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #handlePointerDownOutside = (event) => {
    if (!this.#state.activeItem) return;
    const target = event.target;
    if (target instanceof Node && this.contains(target)) return;

    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();
    this.#deactivate(this.#state.activeItem, { force: true });
  };

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;

    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();
    this.#deactivate(this.#state.activeItem, { force: true });
  };

  /**
   * @param {HTMLElement | null} [item]
   * @param {{ force?: boolean }} [options]
   */
  #deactivate = (item = this.#state.activeItem, options = {}) => {
    if (!item || item != this.#state.activeItem) return;
    if (!options.force && this.overflowHovered) return;

    if (this.#isClosing) {
      if (options.force) {
        this.#clearCloseAnimation();
        this.#finishDeactivate(item);
      }
      return;
    }

    this.#clearCloseAnimation();

    const delay = this.animationDelay;
    if (delay <= 0) {
      this.#finishDeactivate(item);
      return;
    }

    this.#beginCloseAnimation(item);
  };

  /**
   * @param {HTMLElement} item
   */
  #beginCloseAnimation(item) {
    this.#removeCloseTransitionListener();
    this.#isClosing = true;
    this.setAttribute('data-animating', '');
    item.setAttribute('data-animating', '');

    const slotHost = item.closest('.menu-list__list-item');
    const isDefaultSlot = slotHost instanceof HTMLElement ? slotHost.slot === '' : true;
    const panel = this.#resolveSubmenuPanel(item, isDefaultSlot);

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');

    const delay = this.animationDelay;
    this.#closeAnimationTimer = window.setTimeout(() => {
      this.#closeAnimationTimer = null;
      this.#removeCloseTransitionListener();
      if (this.#isClosing) {
        this.#finishDeactivate(item);
      }
    }, delay + 30);

    if (!panel || delay <= 0) return;

    this.#closeTransitionTarget = panel;
    this.#closeTransitionHandler = (event) => {
      if (event.target !== panel || event.propertyName !== 'clip-path') return;
      if (!this.#isClosing) return;

      if (this.#closeAnimationTimer != null) {
        window.clearTimeout(this.#closeAnimationTimer);
        this.#closeAnimationTimer = null;
      }
      this.#removeCloseTransitionListener();
      this.#finishDeactivate(item);
    };
    panel.addEventListener('transitionend', this.#closeTransitionHandler);
  }

  /**
   * @param {HTMLElement} item
   */
  #finishDeactivate(item) {
    this.#isClosing = false;

    if (item !== this.#state.activeItem) {
      item.removeAttribute('data-animating');
      if (!this.#state.activeItem) {
        this.removeAttribute('data-animating');
      }
      return;
    }

    this.dataset.overflowExpanded = 'false';
    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    this.removeAttribute('data-animating');

    this.querySelectorAll('.menu-list__link[aria-expanded="true"]').forEach((link) => {
      link.ariaExpanded = 'false';
      link.removeAttribute('data-animating');
    });

    item.ariaExpanded = 'false';
    item.removeAttribute('data-animating');
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  const item = element.closest('[ref="menuitem"], [role="menuitem"]');

  return item instanceof HTMLElement ? item : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
